import React, { useState } from 'react';
import { Link } from './LocaleLink';
import { TOOLS, type ToolLink } from '../data/tools';

// ---------------------------------------------------------------------------
// Category tags per tool — not shown on cards, used only for filtering.
// ---------------------------------------------------------------------------

type Category = 'Classroom' | 'Events' | 'Games' | 'Gaming' | 'Decisions';

const CATEGORIES: Category[] = ['Classroom', 'Events', 'Games', 'Gaming', 'Decisions'];

const TOOL_CATEGORIES: Record<string, Category[]> = {
  '/image-picker-wheel/':                   ['Classroom', 'Events', 'Decisions'],
  '/weighted-wheel-spinner/':               ['Events', 'Decisions'],
  '/classroom-name-picker/':              ['Classroom'],
  '/random-team-generator/':              ['Classroom', 'Gaming'],
  '/trivia-wheel/':                       ['Classroom'],
  '/giveaway-wheel/':                     ['Events'],
  '/prize-wheel/':                        ['Events'],
  '/spin-the-bottle/':                    ['Events', 'Games'],
  '/wheel-of-fortune-maker/':             ['Games'],
  '/yes-no-wheel/':                       ['Decisions'],
  '/decision-wheel/':                     ['Decisions', 'Gaming'],
  '/number-wheel/':                       ['Decisions'],
  '/random-letter-wheel/':               ['Decisions'],
  // Gaming & Pop Culture cluster
  '/anime-character-picker-wheel/':       ['Gaming'],
  '/fortnite-drop-spot-generator-wheel/': ['Gaming'],
  '/valorant-agent-picker-wheel/':        ['Gaming'],
  '/roblox-game-selector-wheel/':         ['Gaming'],
};

function toolsForCategory(cat: Category | 'All'): ToolLink[] {
  if (cat === 'All') return TOOLS;
  return TOOLS.filter(t => TOOL_CATEGORIES[t.to]?.includes(cat));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PurposeBuiltTools: React.FC = () => {
  const [active, setActive] = useState<Category | 'All'>('All');
  const visible = toolsForCategory(active);

  return (
    <section className="py-14 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white">
            Purpose-built tools
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Whole pages tuned to one job, each preloaded and ready to spin.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            Also in the <strong className="text-gray-500 dark:text-gray-400">Tools</strong> menu above — browse here for detail, or jump from the nav.
          </p>
        </div>

        {/* Filter pills */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {(['All', ...CATEGORIES] as const).map(cat => (
            <button
              key={cat}
              type="button"
              onClick={() => setActive(cat)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all ${
                active === cat
                  ? 'bg-violet-600 text-white shadow-md shadow-violet-500/25'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Card grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map(tool => (
            <Link
              key={tool.to}
              to={tool.to}
              className="group flex items-start gap-3 p-5 rounded-2xl bg-gray-50 dark:bg-gray-950 border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md hover:border-violet-200 dark:hover:border-violet-700 transition-all animate-fade-in"
            >
              <span className="text-2xl leading-none mt-0.5 flex-shrink-0" aria-hidden="true">
                {tool.emoji}
              </span>
              <span className="min-w-0">
                <span className="block font-bold text-gray-900 dark:text-white text-[15px] leading-snug group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                  {tool.label}
                </span>
                <span className="block text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed mt-1">
                  {tool.blurb}
                </span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PurposeBuiltTools;

